import { Modal } from '@constants/Themed';
import { Platform, View, useWindowDimensions } from 'react-native'
import React, { useCallback, useEffect, useState } from 'react';
import WebView from 'react-native-webview';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { ACCEPT_TERMS, HIDE_LOADING_MODAL, HIDE_TC_MODAL, RESET_APP, DECLINE_TERMS, SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import apis from '@api/api';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Checkbox from 'expo-checkbox';
import moment from 'moment';
import { XStack, YStack, Card, Button, SizableText, H3 } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

interface TCModalTypes {
  countryCode?: string,
  userId?: string
}

const TCModal = () => {
  const theme = themeSelector();
  const visible = useSelector((state: DefaultRootState) => state.modals.tcModal?.modalDisplayed);
  const userId = useSelector((state: DefaultRootState) => state.modals.tcModal?.userId);

  // Debug logging
  useEffect(() => {
    console.log('[TCModal] Modal state changed:', { visible, userId });
  }, [visible, userId]); 
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tcVersion, setTCVersion] = useState('Terms and Conditions');
  const [activeCheck, setActiveCheck] = useState(false);
  const { width, height } = useWindowDimensions();
  const [date, setDate] = useState(new Date().toISOString());
  const [error, setError] = useState(false);
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);
  const dispatch = useDispatch();

  const TC_URL = 'https://smartsaas.co.uk/terms-and-conditions?hideNavbar=true';

  const handleGetTerms = async () => {
    setLoading(true);
    setError(false);
    // Set a default version and date
    setTCVersion('Terms and Conditions');
    setDate(new Date().toISOString());
    setLoading(false);
  }


  const activateCheck = useCallback(() => {
    setActiveCheck(true);
  }, [loading])


  const setTC = async () => {
    await apis.updateTC(userId, tcVersion).then(res => {
      if (res.status === 200) {
        // Clear cancellation flag when terms are accepted
        AsyncStorage.removeItem('tc_cancelled').catch(e => {
          console.error('[TCModal] Error clearing cancellation flag:', e);
        });
        console.log('[TCModal] Terms accepted, clearing cancellation flag');
        dispatch({ type: ACCEPT_TERMS });
        setTimeout(() => dispatch({ type: HIDE_TC_MODAL }), 100);
      }
    })
  }


  useEffect(() => {
    handleGetTerms();
    setTimeout(() => activateCheck(), 2000)
  }, [])

  return (
    <Modal
      isVisible={visible}
      animationIn={Platform.OS === 'web' ? 'slideInUp' : 'fadeIn'}
      onBackdropPress={async () => {
        console.log('[TCModal] Backdrop pressed, cancelling TC flow', { userId });

        // Set cancellation flag
        const cancellationTimestamp = Date.now();
        try {
          await AsyncStorage.setItem('tc_cancelled', JSON.stringify({ userId, timestamp: cancellationTimestamp }));
          console.log('[TCModal] Set cancellation flag in AsyncStorage (backdrop)');
        } catch (e) {
          console.error('[TCModal] Error setting cancellation flag:', e);
        }

        // Dispatch DECLINE_TERMS
        dispatch({ type: DECLINE_TERMS });
        await new Promise(resolve => setTimeout(resolve, 50));

        // Clear user data from AsyncStorage
        try {
          await AsyncStorage.removeItem('userData');
          console.log('[TCModal] Cleared userData from AsyncStorage (backdrop)');
        } catch (storageError) {
          console.error('Error clearing userData from storage:', storageError);
        }
        // Clear user data from Redux to prevent automatic navigation
        dispatch({ type: RESET_APP });
        dispatch({ type: HIDE_TC_MODAL });

        // Show auth modal with cancellation message
        setTimeout(() => {
          dispatch({
            type: SHOW_AUTH_MODAL,
            data: {
              content: 'User cancelled',
              type: 'info',
            },
          });
        }, 500);
      }}
      style={{ flex: 1, minWidth: Platform.OS === 'web' ? "90vw" : '100vw', minHeight: "60vh", paddingVertical: Platform.select({ native: '4%', web: '0%' }) }}
      closeButtonStyle={{ top: 60 }}
      deviceWidth={width}
      hideCloseButton={true}
      onModalWillShow={() => dispatch({ type: HIDE_LOADING_MODAL })}
      deviceHeight={height}
    >
      <YStack
        flex={1}
        backgroundColor={theme ? "#ffffff" : "#0f172a"}
        padding="$4"
        gap="$4"
        minWidth={Platform.OS != 'web' ? 400 : '80vw'}
        minHeight="80vh"
        borderRadius={16}
      >
        {/* Header Card */}
        <Card
          backgroundColor={theme ? "#f8fafc" : "#1e293b"}
          borderColor={theme ? "#e2e8f0" : "#334155"}
          borderRadius={16}
          padding="$4"
          elevate
        >
          <XStack alignItems="center" gap="$3" marginBottom="$2">
            <View style={{
              backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
              borderRadius: 12,
              padding: 12
            }}>
              <MaterialIcons
                name="description"
                size={24}
                color={theme ? "#0ea5e9" : "#38bdf8"}
              />
            </View>
            <YStack flex={1}>
              <H3
                color={theme ? "#1e293b" : "#f1f5f9"}
                fontWeight="600"
                fontSize="$5"
              >
                Terms and Conditions
              </H3>
              <SizableText
                size="$3"
                color={theme ? "#64748b" : "#94a3b8"}
                marginTop="$1"
              >
                Please review and accept to proceed
              </SizableText>
            </YStack>
          </XStack>
        </Card>

        {/* Terms Content Card */}
        <Card
          flex={1}
          backgroundColor={theme ? "#ffffff" : "#1e293b"}
          borderColor={theme ? "#e2e8f0" : "#334155"}
          borderRadius={16}
          padding="$3"
          minHeight="50%"
        >
          {!error ? (
            <WebView
              source={{ uri: TC_URL }}
              style={{
                flex: 1,
                backgroundColor: theme ? '#ffffff' : '#1e293b',
                borderRadius: 12
              }}
              containerStyle={{
                flex: 1,
                borderRadius: 12,
                overflow: 'hidden'
              }}
              onLoadStart={() => setLoading(true)}
              onLoadEnd={() => {
                setLoading(false);
              }}
              onError={(syntheticEvent) => {
                const { nativeEvent } = syntheticEvent;
                console.error('WebView error: ', nativeEvent);
                setError(true);
                setLoading(false);
              }}
              injectedJavaScript={`
                (function() {
                  const viewport = document.querySelector('meta[name="viewport"]');
                  if (viewport) {
                    viewport.setAttribute('content', initial-scale=1.0, maximum-scale=1.0');
                  } else {
                    const meta = document.createElement('meta');
                    meta.name = 'viewport';
                    meta.content = initial-scale=1.0, maximum-scale=1.0';
                    document.getElementsByTagName('head')[0].appendChild(meta);
                  }
                  const bgColor = '${theme ? '#ffffff' : '#1e293b'}';
                  const textColor = '${theme ? '#1e293b' : '#f1f5f9'}';
                  const style = document.createElement('style');
                  style.textContent = 'body { background-color: ' + bgColor + ' !important; color: ' + textColor + ' !important; font-size: 14px; line-height: 1.6; padding: 16px; }';
                  document.head.appendChild(style);
                  
                  // Try to extract title from page
                  const title = document.querySelector('h1')?.textContent || document.title || 'Terms and Conditions';
                  window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'title', value: title }));
                  
                  return true;
                })();
              `}
              onMessage={(event) => {
                try {
                  const data = JSON.parse(event.nativeEvent.data);
                  if (data.type === 'title') {
                    setTCVersion(data.value);
                  }
                } catch (e) {
                  // Ignore parse errors
                }
              }}
              startInLoadingState={true}
              scalesPageToFit={true}
            />
          ) : (
            <YStack
              flex={1}
              paddingVertical="$10"
              gap="$6"
              alignItems="center"
              justifyContent="center"
            >
              <MaterialIcons
                name="error-outline"
                size={48}
                color={theme ? "#ef4444" : "#f87171"}
              />
              <SizableText
                size="$4"
                color={theme ? "#1e293b" : "#f1f5f9"}
                textAlign="center"
                paddingHorizontal="$4"
              >
                Could not connect to server to download terms and conditions. Please check your network connection.
              </SizableText>
              <Button
                backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                color="white"
                  onPress={async () => {
                    console.log('[TCModal] Error button clicked, returning to login', { userId });

                    // Set cancellation flag
                    const cancellationTimestamp = Date.now();
                    try {
                      await AsyncStorage.setItem('tc_cancelled', JSON.stringify({ userId, timestamp: cancellationTimestamp }));
                      console.log('[TCModal] Set cancellation flag in AsyncStorage (error button)');
                    } catch (e) {
                      console.error('[TCModal] Error setting cancellation flag:', e);
                    }

                    // Dispatch DECLINE_TERMS
                    dispatch({ type: DECLINE_TERMS });
                    await new Promise(resolve => setTimeout(resolve, 50));

                    // Clear user data from AsyncStorage
                    try {
                      await AsyncStorage.removeItem('userData');
                      console.log('[TCModal] Cleared userData from AsyncStorage (error button)');
                    } catch (storageError) {
                      console.error('Error clearing userData from storage:', storageError);
                    }
                    // Clear user data from Redux to prevent automatic navigation
                    dispatch({ type: RESET_APP });
                    dispatch({ type: HIDE_TC_MODAL });

                    // Show auth modal with cancellation message
                    setTimeout(() => {
                      dispatch({
                        type: SHOW_AUTH_MODAL,
                        data: {
                          content: 'User cancelled',
                          type: 'info',
                        },
                      });
                    }, 500);
                  }}
                borderRadius={12}
                paddingHorizontal="$4"
              >
                <SizableText color="white" size="$3" fontWeight="600">
                  Return to login
                </SizableText>
              </Button>
            </YStack>
          )}
        </Card>

        {/* Agreement Section */}
        <Card
          backgroundColor={theme ? "#f8fafc" : "#1e293b"}
          borderColor={theme ? "#e2e8f0" : "#334155"}
          borderRadius={16}
          padding="$4"
        >
          <YStack gap="$3">
            <XStack
            maxWidth="90%"
              alignItems="center"
              gap="$3"
              paddingBottom="$3"
              borderBottomWidth={1}
              borderBottomColor={theme ? "#e2e8f0" : "#334155"}
            >
              <SizableText
                color={theme ? "#1e293b" : "#f1f5f9"}
                size="$3"
                flex={1}
                adjustsFontSizeToFit
                numberOfLines={2}
              >
                Check this box to agree to our terms and conditions
              </SizableText>
              <Checkbox
                value={checked}
                disabled={!activeCheck}
                onValueChange={setChecked}
                color={checked ? (theme ? "#0ea5e9" : "#38bdf8") : undefined}
              />
            </XStack>

            <XStack gap="$4" justifyContent="center">
              {/* <SizableText
                size="$2"
                color={theme ? "#64748b" : "#94a3b8"}
                fontStyle="italic"
              >
                Version: {tcVersion || 'N/A'}
              </SizableText> */}
              <SizableText
                size="$2"
                color={theme ? "#64748b" : "#94a3b8"}
                fontStyle="italic"
              >
                Date: {date ? moment(date).format(globalDate) : moment().format(globalDate)}
              </SizableText>
            </XStack>
          </YStack>
        </Card>

        {/* Action Buttons */}
        <XStack
          gap="$3"
          justifyContent="center"
          alignItems="center"
          paddingTop="$2"
        >
          <Button
            backgroundColor="#ef4444"
            color="white"
            flex={1}
            maxWidth={200}
            borderRadius={12}
            paddingVertical="$3"
            onPress={async () => {
              console.log('[TCModal] Cancel button clicked', { userId });

              // Set cancellation flag FIRST (before any state changes)
              const cancellationTimestamp = Date.now();
              try {
                await AsyncStorage.setItem('tc_cancelled', JSON.stringify({ userId, timestamp: cancellationTimestamp }));
                console.log('[TCModal] Set cancellation flag in AsyncStorage');
              } catch (e) {
                console.error('[TCModal] Error setting cancellation flag:', e);
              }

              // Dispatch DECLINE_TERMS to mark as intentionally cancelled (prevents error messages)
              // This must happen BEFORE HIDE_TC_MODAL so getTCConfirm can detect it
              dispatch({ type: DECLINE_TERMS });

              // Small delay to ensure DECLINE_TERMS is processed before HIDE_TC_MODAL
              await new Promise(resolve => setTimeout(resolve, 50));

            // Close modal
              dispatch({ type: HIDE_TC_MODAL });

              // Fire API call in background (fire and forget)
              apis.cancelUserCreation(userId).catch((error) => {
                console.error('[TCModal] Error archiving user:', error);
                // Silently fail - user has already closed the modal
              });

              // Clear user data from AsyncStorage
              try {
                await AsyncStorage.removeItem('userData');
                console.log('[TCModal] Cleared userData from AsyncStorage');
              } catch (storageError) {
                console.error('[TCModal] Error clearing userData from storage:', storageError);
              }

              // Clear user data from Redux to prevent automatic navigation
              console.log('[TCModal] Dispatching RESET_APP');
              dispatch({ type: RESET_APP });
            }}
          >
            <XStack alignItems="center" gap="$2">
              <MaterialIcons name="close" size={18} color="white" />
              <SizableText color="white" size="$3" fontWeight="600">
                Cancel
              </SizableText>
            </XStack>
          </Button>

          <Button
            backgroundColor={!checked ? "#64748b" : (theme ? "#0ea5e9" : "#38bdf8")}
            color="white"
            flex={1}
            maxWidth={200}
            borderRadius={12}
            paddingVertical="$3"
            disabled={!checked}
            opacity={checked ? 1 : 0.6}
            onPress={() => setTC()}
          >
            <XStack alignItems="center" gap="$2">
              <MaterialIcons name="check-circle" size={18} color="white" />
              <SizableText color="white" size="$3" fontWeight="600">
                Proceed
              </SizableText>
            </XStack>
          </Button>
        </XStack>
      </YStack>
    </Modal>
  )
}
export default TCModal; 
